/**
 * CancelConfirmationModal Component
 *
 * Confirmation modal for cancelling FleetHub inspection orders.
 * Offers option to reschedule instead of cancelling.
 *
 * @module components/CancelConfirmationModal
 */

import { FC } from "react"
import {
  Modal,
  View,
  ViewStyle,
  TextStyle,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native"
import Icon from "react-native-vector-icons/MaterialIcons"

import { Button } from "@/components/Button"
import { Text } from "@/components/Text"
import { colors, spacing } from "@/theme"

export interface CancelConfirmationModalProps {
  /**
   * Whether the modal is visible
   */
  visible: boolean

  /**
   * Callback when modal is closed
   */
  onClose: () => void

  /**
   * Callback when user confirms cancellation
   */
  onConfirmCancel: () => void

  /**
   * Callback when user chooses to reschedule instead
   */
  onRescheduleInstead: () => void

  /**
   * Loading state during cancellation
   */
  isLoading?: boolean

  /**
   * Error message if cancellation fails
   */
  errorMessage?: string | null
}

/**
 * Cancel Confirmation Modal
 *
 * Shows confirmation with option to reschedule instead of cancelling.
 */
export const CancelConfirmationModal: FC<CancelConfirmationModalProps> = ({
  visible,
  onClose,
  onConfirmCancel,
  onRescheduleInstead,
  isLoading = false,
  errorMessage = null,
}) => {
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={$modalOverlay}>
        <TouchableOpacity style={$overlayTouchable} activeOpacity={1} onPress={onClose} />

        <View style={$modalContainer}>
          {/* Header */}
          <View style={$header}>
            <Text style={$headerTitle}>Auftrag stornieren?</Text>
            <TouchableOpacity onPress={onClose} style={$closeButton}>
              <Icon name="close" size={24} color={colors.text} />
            </TouchableOpacity>
          </View>

          {/* Content */}
          <View style={$content}>
            <Text style={$message}>
              Möchtest du den Auftrag wirklich stornieren? Du kannst diesen auch direkt verschieben.
            </Text>

            {/* Error Message */}
            {errorMessage && (
              <View style={$errorContainer}>
                <Icon name="error-outline" size={20} color={colors.error} />
                <Text style={$errorText}>{errorMessage}</Text>
              </View>
            )}

            {/* Action Buttons */}
            <View style={$buttonContainer}>
              {/* Reschedule Button - Highlighted in red */}
              <Button
                text="Verschieben"
                onPress={onRescheduleInstead}
                preset="filled"
                style={$rescheduleButton}
                disabled={isLoading}
              />

              {/* Cancel Button - Neutral black */}
              <Button
                text={isLoading ? "Wird storniert..." : "Stornieren"}
                onPress={onConfirmCancel}
                preset="default"
                style={$cancelButton}
                disabled={isLoading}
                RightAccessory={
                  isLoading
                    ? () => <ActivityIndicator size="small" color={colors.text} />
                    : undefined
                }
              />
            </View>
          </View>
        </View>
      </View>
    </Modal>
  )
}

// Styles
const $modalOverlay: ViewStyle = {
  flex: 1,
  backgroundColor: "rgba(17, 30, 44, 0.5)",
  justifyContent: "center",
  alignItems: "center",
  padding: spacing.xl,
}

const $overlayTouchable: ViewStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
}

const $modalContainer: ViewStyle = {
  width: "100%",
  maxWidth: 500,
  backgroundColor: colors.surface,
  borderRadius: 16,
  // @ts-ignore - Web-specific shadow
  boxShadow: "0 4px 24px rgba(17, 30, 44, 0.2)",
  elevation: 8,
  overflow: "hidden",
}

const $header: ViewStyle = {
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  paddingHorizontal: spacing.xl,
  paddingVertical: spacing.lg,
  borderBottomWidth: 1,
  borderBottomColor: colors.palette.neutral200,
}

const $headerTitle: TextStyle = {
  fontSize: 24,
  fontWeight: "700",
  color: colors.text,
}

const $closeButton: ViewStyle = {
  padding: spacing.xs,
  borderRadius: 8,
  cursor: "pointer" as any,
}

const $content: ViewStyle = {
  paddingHorizontal: spacing.xl,
  paddingVertical: spacing.lg,
}

const $message: TextStyle = {
  fontSize: 16,
  color: colors.text,
  lineHeight: 24,
  marginBottom: spacing.lg,
}

const $errorContainer: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  backgroundColor: colors.errorBgLight,
  padding: spacing.sm,
  borderRadius: 8,
  marginBottom: spacing.md,
}

const $errorText: TextStyle = {
  fontSize: 14,
  color: colors.error,
  marginLeft: spacing.xs,
  flex: 1,
}

const $buttonContainer: ViewStyle = {
  flexDirection: "row",
  gap: spacing.sm,
}

const $rescheduleButton: ViewStyle = {
  flex: 1,
  backgroundColor: colors.error,
  minHeight: 48,
}

const $cancelButton: ViewStyle = {
  flex: 1,
  backgroundColor: colors.palette.neutral800,
  borderWidth: 1,
  borderColor: colors.palette.neutral700,
  minHeight: 48,
}
